import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { COMPANY, LOGOS } from "@/lib/company";
import { WindowDiagram } from "@/components/WindowDiagram";
import { AuthedImage } from "@/components/AuthedImage";
import { TYPOLOGY_DETAILS } from "@/lib/typologyData";
import { fmtMoney, fmtNum } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Printer, FileSpreadsheet, Pencil, Loader2 } from "lucide-react";

export default function QuotationPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState(null);
  const [mode, setMode] = useState("client");
  const [customSpecs, setCustomSpecs] = useState({});

  useEffect(() => {
    api
      .get(`/quotations/${id}`)
      .then(({ data }) => setQ(data))
      .catch(() => toast.error("Failed to load quotation"));
    api
      .get("/rates")
      .then(({ data }) => {
        const map = {};
        (data.custom_typologies || []).forEach((c) => {
          map[c.typology] = {
            system: "Custom Typology",
            frame_depth: c.frame_depth || "",
            glazing: c.glazing || "",
            hardware: c.hardware || "",
            drawing_file_id: c.drawing_file_id || null,
            drawing_filename: c.drawing_filename || "",
          };
        });
        setCustomSpecs(map);
      })
      .catch(() => {});
  }, [id]);

  const downloadExcel = async () => {
    try {
      const res = await api.get(`/quotations/${id}/export/excel`, {
        params: { copy: mode },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(q?.quote_no || "quotation").replace(/\//g, "_")}${mode === "internal" ? "_INTERNAL" : ""}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Excel export failed");
    }
  };

  if (!q) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  const t = q.totals || {};

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[900px] mx-auto px-4 h-16 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="rounded-sm" data-testid="preview-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <div className="flex items-center rounded-sm border border-slate-200 overflow-hidden text-xs mr-1" data-testid="preview-mode-toggle">
              <button
                data-testid="preview-mode-client"
                onClick={() => setMode("client")}
                className={`px-3 py-1.5 font-semibold transition-colors ${mode === "client" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                Client
              </button>
              <button
                data-testid="preview-mode-internal"
                onClick={() => setMode("internal")}
                className={`px-3 py-1.5 font-semibold transition-colors ${mode === "internal" ? "bg-amber-500 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              >
                Internal
              </button>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/quotation/${id}/edit`)} className="rounded-sm">
              <Pencil className="w-4 h-4 mr-1.5" /> Edit
            </Button>
            <Button variant="outline" size="sm" onClick={downloadExcel} className="rounded-sm" data-testid="preview-excel">
              <FileSpreadsheet className="w-4 h-4 mr-1.5 text-emerald-600" /> Excel
            </Button>
            <Button size="sm" onClick={() => window.print()} className="rounded-sm bg-blue-600 hover:bg-blue-700" data-testid="preview-print">
              <Printer className="w-4 h-4 mr-1.5" /> {mode === "internal" ? "Internal PDF" : "Client PDF"}
            </Button>
          </div>
        </div>
      </div>

      {/* A4 document */}
      <div className="py-8 px-4 flex justify-center">
        <div
          className="print-page bg-white shadow-xl w-full max-w-[800px] text-slate-800"
          style={{ fontSize: "12px" }}
          data-testid="quotation-document"
        >
          <div className="p-10">
            {mode === "internal" && (
              <div
                data-testid="internal-banner"
                className="mb-4 border border-amber-300 bg-amber-50 text-amber-800 text-[11px] font-bold uppercase tracking-wider px-3 py-2 rounded-sm text-center"
              >
                Internal Copy — Confidential · Includes Margin · Not for Client
              </div>
            )}
            {/* Header */}
            <div className="flex items-start justify-between pb-4 border-b-2 border-slate-900">
              <div className="flex flex-col gap-1.5">
                <img src={LOGOS.agi} alt="Artisan Guild Infra" className="h-12 w-auto object-contain" />
                <div className="text-[11px] text-blue-600 font-medium">{COMPANY.system}</div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <div className="flex items-center gap-3">
                  <img src={LOGOS.bhoruka} alt="Bhoruka" className="h-9 w-auto object-contain" />
                  <img src={LOGOS.ykk} alt="YKK AP" className="h-9 w-auto object-contain" />
                </div>
                <div className="text-right text-[9px] text-slate-500 leading-relaxed">
                  <div>{COMPANY.email}</div>
                  <div>{COMPANY.website}</div>
                </div>
              </div>
            </div>

            {/* Meta grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 mt-5 text-[11px]">
              <Meta label="Quote No" value={q.quote_no} mono />
              <Meta label="Date" value={q.quote_date} mono />
              <Meta label="Prepared By" value={q.prepared_by} />
              <Meta label="Client" value={q.client_name} />
              <Meta label="Location" value={q.client_location} />
              <Meta label="Project" value={q.project_title} span />
            </div>

            {q.subject && (
              <div className="mt-4 text-[11px]">
                <span className="font-semibold text-slate-700">Subject: </span>
                {q.subject}
                {q.client_name ? ` in Residence of ${q.client_name}` : ""}
                {q.client_location ? ` at ${q.client_location}.` : "."}
              </div>
            )}
            {q.intro && <p className="mt-3 text-[11px] leading-relaxed text-slate-600">{q.intro}</p>}

            {/* BOQ */}
            <div className="mt-6">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-900 mb-2">
                Bill of Quantities
              </div>
              <table className="w-full border-collapse text-[9.5px]">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    {["Code", "Elevation", "Nos", "Glass", "Remark", "Mesh", "W (mm)", "H (mm)", "Sqft", "Rate", "Amount"].map((h) => (
                      <th key={h} className="border border-slate-700 px-1.5 py-1.5 font-semibold text-center">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(q.items || []).map((it, i) => {
                    const gf = 1 + (Number(t.global_margin_percent) || 0) / 100;
                    const internal = mode === "internal";
                    const dispAmt = internal ? (Number(it.amount) || 0) : (Number(it.amount) || 0) * gf;
                    const dispRate = internal
                      ? (Number(it.rate) || 0)
                      : (Number(it.qty_sqft) > 0 ? dispAmt / Number(it.qty_sqft) : (Number(it.rate) || 0) * gf);
                    return (
                    <tr key={it.id || i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                      <td className="border border-slate-200 px-1.5 py-1 font-mono font-semibold text-center align-top">
                        {it.code}
                      </td>
                      <td className="border border-slate-200 px-1 py-1 align-middle">
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-[86px] h-[70px]">
                            {customSpecs[it.typology]?.drawing_file_id ? (
                              <AuthedImage
                                fileId={customSpecs[it.typology].drawing_file_id}
                                filename={customSpecs[it.typology].drawing_filename}
                                className="w-full h-full object-contain"
                              />
                            ) : (
                              <WindowDiagram
                                typology={it.typology}
                                width={it.width_mm}
                                height={it.height_mm}
                                className="w-full h-full"
                                showDims={false}
                              />
                            )}
                          </div>
                          <span className="font-mono text-[8px] text-slate-500">{it.elevation}</span>
                        </div>
                      </td>
                      <td className="border border-slate-200 px-1.5 py-1 text-center font-mono align-top">{it.nos}</td>
                      <td className="border border-slate-200 px-1.5 py-1 align-top">
                        {[it.glass_type, ...(it.extra_glasses || [])].filter(Boolean).map((g, gi) => (
                          <div key={gi}>{g}</div>
                        ))}
                      </td>
                      <td className="border border-slate-200 px-1.5 py-1 align-top whitespace-pre-wrap">{it.remark || "—"}</td>
                      <td className="border border-slate-200 px-1.5 py-1 text-center align-top">{it.fly_mesh || "—"}</td>
                      <td className="border border-slate-200 px-1.5 py-1 text-center font-mono align-top">{fmtNum(it.width_mm)}</td>
                      <td className="border border-slate-200 px-1.5 py-1 text-center font-mono align-top">{fmtNum(it.height_mm)}</td>
                      <td className="border border-slate-200 px-1.5 py-1 text-right font-mono align-top">{fmtNum(it.qty_sqft)}</td>
                      <td className="border border-slate-200 px-1.5 py-1 text-right font-mono align-top">{fmtNum(dispRate)}</td>
                      <td className="border border-slate-200 px-1.5 py-1 text-right font-mono font-semibold align-top bg-slate-50">
                        {fmtMoney(dispAmt)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end mt-3">
                <table className="text-[11px] w-[300px]">
                  <tbody>
                    {mode === "internal" ? (
                      <>
                        <TotRow l="Basic Amount" v={t.basic_subtotal} />
                        {t.line_margin_total ? <TotRow l="Line Margin" v={t.line_margin_total} /> : null}
                        {t.global_margin_amount ? (
                          <TotRow l={`Margin on Basic @ ${t.global_margin_percent}%`} v={t.global_margin_amount} />
                        ) : null}
                        <TotRow l="Sub Total (Taxable)" v={t.taxable} bold />
                      </>
                    ) : (
                      <TotRow l="Total (Pre-Tax)" v={t.taxable} bold />
                    )}
                    <TotRow l={`GST @ ${t.gst_percent}%`} v={t.gst_amount} />
                    <tr className="bg-blue-600 text-white">
                      <td className="px-2 py-1.5 font-bold uppercase tracking-wider text-[10px]">Grand Total</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold">₹ {fmtMoney(t.grand_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="text-[9px] text-slate-400 mt-1 text-right">
                {t.total_units} units · {fmtNum(t.total_sqft)} sqft
              </div>
            </div>

            {/* System specifications for typologies used */}
            {(() => {
              const detailsFor = (t) => TYPOLOGY_DETAILS[t] || customSpecs[t];
              const used = [...new Set((q.items || []).map((it) => it.typology))].filter((t) => detailsFor(t));
              if (!used.length) return null;
              return (
                <div className="mt-8 avoid-break" data-testid="system-specs">
                  <SectionTitle>System Specifications — IWIN-S</SectionTitle>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {used.map((t) => {
                      const d = detailsFor(t);
                      return (
                        <div key={t} className="border border-slate-200 rounded-sm p-3">
                          <div className="font-semibold text-slate-800 text-[11px]">
                            {t} <span className="text-slate-400 font-normal">· {d.system}</span>
                          </div>
                          <div className="mt-1 text-[10px] text-slate-600 space-y-0.5">
                            {d.frame_depth && <div>Frame depth: {d.frame_depth}{d.interlock ? ` · Interlock ${d.interlock}` : ""}</div>}
                            {d.glazing && <div>Glazing: {d.glazing}</div>}
                            {d.size && <div>Size envelope: {d.size}</div>}
                            {d.performance && <div>Performance: {d.performance}</div>}
                            {d.load && <div>{d.load}</div>}
                            {d.hardware && <div>Hardware: {d.hardware}</div>}
                            {d.features && <div className="text-slate-500">{d.features.join(" · ")}</div>}
                          </div>
                          {d.drawing_file_id && (
                            <div className="mt-2 border border-slate-200 rounded-sm overflow-hidden h-[110px] bg-slate-50" data-testid={`custom-typo-ref-${t.replace(/ /g, "-")}`}>
                              <AuthedImage fileId={d.drawing_file_id} filename={d.drawing_filename} className="w-full h-full object-contain" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Technical specs */}
            {q.technical_specs && (
              <div className="mt-8 page-break avoid-break">
                <SectionTitle>Technical Specifications</SectionTitle>
                <pre className="whitespace-pre-wrap font-sans text-[10px] leading-relaxed text-slate-600 mt-2">
                  {q.technical_specs}
                </pre>
              </div>
            )}

            {/* Warranty */}
            {q.warranty?.length > 0 && (
              <div className="mt-6 avoid-break">
                <SectionTitle>Warranty</SectionTitle>
                <ol className="list-decimal ml-5 mt-2 space-y-1 text-[10.5px] text-slate-600">
                  {q.warranty.map((w, i) => <li key={i}>{w}</li>)}
                </ol>
              </div>
            )}

            {/* Payment */}
            {q.payment_terms?.length > 0 && (
              <div className="mt-6 avoid-break">
                <SectionTitle>Payment Terms</SectionTitle>
                <ol className="list-decimal ml-5 mt-2 space-y-1 text-[10.5px] text-slate-600">
                  {q.payment_terms.map((w, i) => <li key={i}>{w}</li>)}
                </ol>
              </div>
            )}

            {/* Bank Details */}
            {q.bank_details?.trim() && (
              <div className="mt-6 avoid-break" data-testid="bank-details-section">
                <SectionTitle>Bank Account Details</SectionTitle>
                <pre className="whitespace-pre-wrap font-sans text-[10.5px] leading-relaxed text-slate-600 mt-2">
                  {q.bank_details}
                </pre>
              </div>
            )}

            {/* Terms */}
            {q.terms?.length > 0 && (
              <div className="mt-6 avoid-break">
                <SectionTitle>Standard Terms & Conditions</SectionTitle>
                <ol className="list-decimal ml-5 mt-2 space-y-1 text-[10px] text-slate-600">
                  {q.terms.map((w, i) => <li key={i}>{w}</li>)}
                </ol>
              </div>
            )}

            {/* Signature */}
            <div className="mt-10 flex items-end justify-between avoid-break">
              <div className="text-[10px] text-slate-500 leading-relaxed">
                <div>{COMPANY.address_1}</div>
                <div>{COMPANY.address_2}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-slate-500 mb-6">Thanks & Regards,</div>
                <div className="font-display font-bold text-slate-900">{COMPANY.ceo_name}</div>
                <div className="text-[10px] text-slate-500">{COMPANY.ceo_title}, {COMPANY.name}</div>
                <div className="text-[10px] text-slate-500 font-mono">{COMPANY.ceo_phone}</div>
              </div>
            </div>

            {(q.items || []).some((it) => it.drawing_file_id && !it.hide_annexure) && (
              <div className="mt-8">
                {(q.items || [])
                  .filter((it) => it.drawing_file_id && !it.hide_annexure)
                  .map((it, i) => (
                    <div key={it.id || i} className="page-break avoid-break pt-4" data-testid={`annexure-${i}`}>
                      <SectionTitle>
                        Annexure — Reference Drawing {it.code ? `(${it.code})` : ""}
                      </SectionTitle>
                      <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-[11px] text-slate-600">
                        <span><b>Typology:</b> {it.typology}</span>
                        <span><b>Elevation:</b> {it.elevation || "—"}</span>
                        <span className="font-mono"><b>Size:</b> {fmtNum(it.width_mm)} × {fmtNum(it.height_mm)} mm</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-4">
                        <div className="border border-slate-200 rounded-sm bg-white p-3 flex flex-col">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Generated Elevation</div>
                          <div className="flex-1 flex items-center justify-center" style={{ minHeight: "360px" }}>
                            <WindowDiagram
                              typology={it.typology}
                              width={it.width_mm}
                              height={it.height_mm}
                              code={it.code}
                              elevation={it.elevation}
                              className="w-full h-full"
                            />
                          </div>
                          {TYPOLOGY_DETAILS[it.typology] && (
                            <div className="mt-2 text-[9px] text-slate-500 border-t border-slate-100 pt-2">
                              {TYPOLOGY_DETAILS[it.typology].system} · Frame {TYPOLOGY_DETAILS[it.typology].frame_depth} · {TYPOLOGY_DETAILS[it.typology].glazing}
                            </div>
                          )}
                        </div>
                        <div className="border border-slate-200 rounded-sm bg-slate-50 p-3 flex flex-col">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2">Uploaded Reference</div>
                          <div className="flex-1 flex items-center justify-center" style={{ minHeight: "360px" }}>
                            <AuthedImage
                              fileId={it.drawing_file_id}
                              filename={it.drawing_filename}
                              className="max-h-[420px] max-w-full object-contain"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value, mono, span }) {
  return (
    <div className={span ? "col-span-2 md:col-span-3" : ""}>
      <span className="text-[9px] uppercase tracking-wider text-slate-400 block">{label}</span>
      <span className={`text-slate-800 font-medium ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-900 border-b border-slate-200 pb-1">
      {children}
    </div>
  );
}

function TotRow({ l, v, bold }) {
  return (
    <tr className={bold ? "border-t border-slate-300" : ""}>
      <td className={`px-2 py-1 ${bold ? "font-semibold text-slate-800" : "text-slate-600"}`}>{l}</td>
      <td className={`px-2 py-1 text-right font-mono ${bold ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        ₹ {fmtMoney(v)}
      </td>
    </tr>
  );
}
